// ProfileSettings.tsx - Profile settings component
import { useState, useEffect } from 'react';
import {
  Paper,
  Stack,
  TextInput,
  Button,
  Text,
  Title,
  Select,
  Avatar,
  Group,
  Divider,
  Alert,
} from '@mantine/core';
import {
  IconUser,
  IconMail,
  IconPhone,
  IconBuilding,
  IconCheck,
  IconPhoto,
  IconUpload,
} from '@tabler/icons-react';
import { FileUploader } from '../storage/FileUploader';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  phone?: string;
  companyName?: string;
  companySize?: string;
  timezone?: string;
  language?: string;
}

export function ProfileSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    companySize: '',
    timezone: 'UTC',
    language: 'en',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://carleads-api-staging.carleads.workers.dev'}/api/auth/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (data.success && data.data) {
        const user = data.data;
        setProfile(user);
        setFormData({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          companyName: user.companyName || '',
          companySize: user.companySize || '',
          timezone: user.timezone || 'UTC',
          language: user.language || 'en',
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://carleads-api-staging.carleads.workers.dev'}/api/users/profile`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        if (data.data) {
          setProfile(data.data);
          setFormData({
            name: data.data.name || '',
            email: data.data.email || '',
            phone: data.data.phone || '',
            companyName: data.data.companyName || '',
            companySize: data.data.companySize || '',
            timezone: data.data.timezone || 'UTC',
            language: data.data.language || 'en',
          });
        }
      } else {
        setError(data.error || 'Failed to update profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = (files: any[]) => {
    if (files.length > 0 && profile) {
      setProfile({ ...profile, avatarUrl: files[0].url });
      setSuccess(true);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Paper p="xl" radius="md" withBorder>
      <Stack gap="lg">
        <div>
          <Title order={2} mb="xs">
            Profile Settings
          </Title>
          <Text c="dimmed" size="sm">
            Update your personal information and preferences.
          </Text>
        </div>

        {error && (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        )}

        {success && (
          <Alert icon={<IconCheck size={16} />} color="green" title="Success">
            Profile updated successfully!
          </Alert>
        )}

        {/* Avatar Upload */}
        <div>
          <Text size="sm" fw={600} mb="xs">
            Profile Picture
          </Text>
          <Group gap="md">
            <Avatar
              src={profile?.avatarUrl}
              size={80}
              radius="md"
              color="blue"
            >
              {profile?.name?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <div style={{ flex: 1 }}>
              <FileUploader
                accept="image/*"
                maxSize={5}
                multiple={false}
                entityType="user_avatar"
                entityId={profile?.id}
                onUploadComplete={handleAvatarUpload}
              />
            </div>
          </Group>
        </div>

        <Divider />

        {/* Profile Form */}
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <TextInput
              label="Full Name"
              placeholder="Your name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              leftSection={<IconUser size={16} />}
              disabled={saving}
            />

            <TextInput
              label="Email Address"
              placeholder="your@email.com"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              leftSection={<IconMail size={16} />}
              disabled={saving}
            />

            <TextInput
              label="Phone Number"
              placeholder="+37120000000"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              leftSection={<IconPhone size={16} />}
              disabled={saving}
            />

            <TextInput
              label="Company Name"
              placeholder="Your company"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              leftSection={<IconBuilding size={16} />}
              disabled={saving}
            />

            <Select
              label="Company Size"
              placeholder="Select size"
              data={[
                { value: '1-10', label: '1-10 employees' },
                { value: '11-50', label: '11-50 employees' },
                { value: '51-200', label: '51-200 employees' },
                { value: '201-500', label: '201-500 employees' },
                { value: '500+', label: '500+ employees' },
              ]}
              value={formData.companySize}
              onChange={(value) => setFormData({ ...formData, companySize: value || '' })}
              disabled={saving}
            />

            <Select
              label="Timezone"
              placeholder="Select timezone"
              data={[
                { value: 'UTC', label: 'UTC' },
                { value: 'Europe/Riga', label: 'Europe/Riga' },
                { value: 'Europe/London', label: 'Europe/London' },
                { value: 'America/New_York', label: 'America/New_York' },
              ]}
              value={formData.timezone}
              onChange={(value) => setFormData({ ...formData, timezone: value || 'UTC' })}
              disabled={saving}
            />

            <Select
              label="Language"
              placeholder="Select language"
              data={[
                { value: 'en', label: 'English' },
                { value: 'lv', label: 'Latvian' },
                { value: 'de', label: 'German' },
              ]}
              value={formData.language}
              onChange={(value) => setFormData({ ...formData, language: value || 'en' })}
              disabled={saving}
            />

            <Button type="submit" loading={saving} fullWidth>
              Save Changes
            </Button>
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
}
